import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageChrome, ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { useAuthStore } from '@/store/auth'
import { useSeedBalance } from '@/hooks/useSeeds'
import {
  useWatchState, useSetWatch, useMoveBudget, useAddWatch, useRemoveWatch, useAddWatchType, useRemoveWatchType,
  useSendTestAlert, useScanNow, useUnreadAlerts,
} from '@/hooks/useAlerts'
import { WATCH_TYPES, type WatchTypeId, type WatchState } from '@/components/alerts/types'
import { SEED, Sheet, useToast } from '@/components/alerts/ui'
import { topicBackground } from '@/components/gg/TopicScene'
import { TopicGlyph } from '@/components/gg/TopicGlyph'
import '@/styles/alerts.css'

const Mega = ({ off }: { off?: boolean }) => <svg className={off ? 'off' : undefined} viewBox="0 0 24 24" aria-hidden="true"><path d="M3 10v4h3l6 4V6L6 10H3z" /><path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a7.5 7.5 0 0 1 0 11" /></svg>
const Bell = ({ off }: { off?: boolean }) => <svg className={off ? 'off' : undefined} viewBox="0 0 24 24" aria-hidden="true"><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15L6 16z" /><path d="M10 20.5a2 2 0 0 0 4 0" /></svg>
const day = (t: string) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

/**
 * Breaking news (/app/feed/breaking). A feed control. Brian's
 * settings-breaking.html v1.4 (and Oct 22: on a computer the watched types sit
 * in a grid of photo cards).
 *
 * One alert budget for every type: 1 seed per delivered alert; Build costs 12
 * from the week, only if asked. The switch is the pause (with its sheet). + and
 * − move 5 to and from this week's seeds. Auto top-up refills to the cap at the
 * floor. Watched types: national topics and, where a place has a feed, Safety
 * and Weather. A watch is a subject plus a condition.
 */
export function BreakingSettingsPage() {
  const desk = useIsDesktop()
  const { data: w } = useWatchState()
  const waiting = useUnreadAlerts()
  const toast = useToast()
  const [sheet, setSheet] = useState<null | { kind: 'pause' } | { kind: 'add'; type: WatchTypeId } | { kind: 'another' }>(null)
  const close = () => setSheet(null)

  return (
    <>
      <PageChrome title="Breaking news" navKey="breaking" back={{ to: ROUTES.feedControls, label: 'Feed controls' }}
        actions={desk ? <Link className="gg-act" to={ROUTES.alerts}>Breaking alerts{waiting ? ` · ${waiting}` : ''}</Link> : undefined} />
      <div className={`gg gga${desk ? ' gga--wide' : ''}`}>
        <p className="gga-lead">Get stories the moment they happen: a storm warning, a road closure, a big local event. Each alert you get uses 1 seed from a small budget set aside for alerts; building one into a story is 12, only if you choose to.</p>
        {w && (
          <div style={desk ? { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12, alignItems: 'start' } : undefined}>
            <BudgetCard w={w} onPause={() => setSheet({ kind: 'pause' })} toast={toast.show} />
            <PushCard w={w} />
          </div>
        )}
        {w && (
          <>
            <div className="gga-sect">Watched types</div>
            <div className="gga-types">
              {w.types.map((t) => <TypeCard key={t} w={w} type={t} onAdd={() => setSheet({ kind: 'add', type: t })} />)}
            </div>
            {Object.keys(WATCH_TYPES).some((k) => !w.types.includes(k as WatchTypeId)) && (
              <button type="button" className="gga-another" onClick={() => setSheet({ kind: 'another' })}><i>+</i>Watch another content type</button>
            )}
          </>
        )}
        {!desk && <Link className="gga-another" to={ROUTES.alerts} style={{ textDecoration: 'none' }}>Breaking alerts{waiting ? ` · ${waiting} waiting` : ''}</Link>}
        <DevTest />
      </div>
      {w && <PauseSheet open={sheet?.kind === 'pause'} onClose={close} toast={toast.show} />}
      {w && sheet?.kind === 'add' && <AddSheet type={sheet.type} onClose={close} toast={toast.show} />}
      {w && <AnotherSheet open={sheet?.kind === 'another'} w={w} onClose={close} onPick={(t) => setSheet({ kind: 'add', type: t })} />}
      {toast.node}
    </>
  )
}

function BudgetCard({ w, onPause, toast }: { w: WatchState; onPause: () => void; toast: (b: React.ReactNode) => void }) {
  const set = useSetWatch()
  const move = useMoveBudget()
  const { data: bal } = useSeedBalance()
  const [floor, setFloor] = useState<number | null>(null)
  const on = !w.paused
  const weekly = bal?.weekly_remaining ?? 0
  const pct = Math.min(100, (w.bank / Math.max(1, w.cap)) * 100)
  const f = floor ?? w.floor
  const zero = w.bank === 0, low = w.bank > 0 && w.bank <= w.floor
  const last = w.history[0]
  return (
    <section className={`gga-cardbox${on ? '' : ' off'}`}>
      <div className="gga-row">
        <Mega off={!on} />
        <div className="t"><div className="n">Alert budget</div><div className={`s${on ? '' : ' off'}`}>{on ? 'Active, matching on every type' : 'Paused, balance kept'}</div></div>
        <button className="gg-switch" type="button" role="switch" aria-checked={on} aria-label="Watching"
          onClick={() => { if (w.paused) { set.mutate({ paused: false }); toast(<>Watching again. <b>Nothing that broke while paused comes back.</b></>) } else onPause() }} />
      </div>
      <div className="gga-bank">
        <div className="t">
          <div className="k">Shared across every watched type</div>
          <div className={`v${zero ? ' zero' : low ? ' low' : ''}`}>{SEED}<b>{w.bank}</b><small>/ {w.cap}</small></div>
          <div className={`gga-meter${on ? '' : ' off'}`}><i style={{ width: `${pct}%` }} />{w.auto && on && <u style={{ left: `calc(${(f / Math.max(1, w.cap)) * 100}% - 1px)` }} />}</div>
        </div>
        <button className="gga-step" type="button" aria-label="Move 5 back to this week" disabled={w.movable_out <= 0 || move.isPending}
          onClick={() => move.mutate(-1, { onSuccess: (n) => { if (n) toast(`${-n} seeds back in your week.`) } })}>−</button>
        <button className="gga-step plus" type="button" aria-label="Move 5 in from this week" disabled={w.bank >= w.cap || weekly <= 0 || move.isPending}
          onClick={() => move.mutate(1)}>+</button>
      </div>
      <p className="gga-fine">1 seed per verified match, whichever type fired. Move 5 at a time from your weekly seeds; seeds moved back go back into your week, and like all your weekly seeds, unused ones roll over (up to a week’s worth).</p>
      {zero && <div className="gga-warn zero">Your alert budget is empty. Matching has stopped, and stories that break now are not backfilled. <Link to={`${ROUTES.alerts}?refill=1`}>Refill</Link></div>}
      {!zero && w.bank === 1 && on && <div className="gga-warn">One seed left. The next match uses it, then matching stops until the budget is refilled.</div>}
      <div className="gga-hr" />
      <div className="gga-row">
        <div className="t"><div className="n" style={{ fontSize: 14 }}>Auto top-up</div>
          <div className="p">{!on ? 'Off while paused' : w.auto ? `Refills to ${w.cap} when the budget hits ${f}` : 'Off. The budget can run dry'}</div></div>
        <button className="gg-switch gga-switch-y" type="button" role="switch" aria-checked={w.auto && on} aria-label="Auto top-up" disabled={!on}
          onClick={() => set.mutate({ auto: !w.auto })} />
      </div>
      <div className="gga-floor">
        <span className="k">Floor</span>
        <input type="range" min={0} max={10} step={1} value={f} aria-label="Top-up floor" disabled={!(w.auto && on)}
          onChange={(e) => setFloor(+e.target.value)}
          onPointerUp={() => floor != null && set.mutate({ floor }, { onSettled: () => setFloor(null) })}
          onKeyUp={() => floor != null && set.mutate({ floor }, { onSettled: () => setFloor(null) })} />
        <span className={`v${w.auto && on ? '' : ' off'}`}>{f} / 10</span>
      </div>
      <p className="gga-fine">When the budget drops to {f}, it refills to {w.cap} with seeds moved from this week’s. Nothing extra is charged, but those seeds come out of your week. Off while paused.</p>
      {last && <div className="gga-hist">{last.kind === 'fill' ? `Started at ${last.bank}` : `Topped up to ${last.bank}`} · {day(last.at)}</div>}
    </section>
  )
}

function PushCard({ w }: { w: WatchState }) {
  const set = useSetWatch()
  return (
    <section className="gga-cardbox">
      <div className="gga-row">
        <Bell off={!w.push} />
        <div className="t"><div className="n" style={{ fontSize: 14 }}>Push notifications</div>
          <div className="p">{w.push ? 'On. Follows your account setting. Alerts carry the headline.' : 'Off. Alerts wait in your queue with no notification.'}</div></div>
        <button className="gg-switch" type="button" role="switch" aria-checked={w.push} aria-label="Push notifications" onClick={() => set.mutate({ push: !w.push })} />
      </div>
    </section>
  )
}

function TypeCard({ w, type, onAdd }: { w: WatchState; type: WatchTypeId; onAdd: () => void }) {
  const remove = useRemoveWatch()
  const removeType = useRemoveWatchType()
  const T = WATCH_TYPES[type]
  if (!T) return null
  const on = !w.paused
  const list = w.watches.filter((x) => x.type === type)
  return (
    <section className={`gga-type${on ? '' : ' off'}`}>
      <div className="ph" style={{ backgroundImage: topicBackground(type), backgroundPosition: 'center top, center' }} />
      <div className="band">
        <div className="gga-row">
          <div className="t"><div className="n">{T.name}</div>
            <div className={`s${on ? '' : ' off'}`}>{on ? (list.length === 1 ? '1 watch' : `${list.length} watches`) : 'Paused'}{T.scope === 'local' ? ' · local feed' : ''}</div></div>
          {!list.length && <button className="rm" type="button" aria-label={`Stop watching ${T.name}`} onClick={() => removeType.mutate(type)}>×</button>}
        </div>
      </div>
      <div className="glass">
        <div className="head2"><span>Watching</span><span>subject · condition</span></div>
        <div className="gga-watches">
          {list.map((x) => (
            <span className="gga-wpill" key={x.id}>
              <span className="sj">{x.subject}</span><span className="dot">·</span><span className={`c${x.own ? ' free' : ''}`}>{x.condition}</span>
              <button type="button" aria-label={`Remove ${x.subject} · ${x.condition}`} onClick={() => remove.mutate(x.id)}>×</button>
            </span>
          ))}
          <button className="gga-addw" type="button" disabled={!on} onClick={onAdd}>Add watch <b>+</b></button>
        </div>
      </div>
    </section>
  )
}

// §7.2: a small sheet, not a warning
function PauseSheet({ open, onClose, toast }: { open: boolean; onClose: () => void; toast: (b: React.ReactNode) => void }) {
  const set = useSetWatch()
  return (
    <Sheet open={open} onClose={onClose} label="Pause breaking news?">
      <h3>Pause breaking news?</h3>
      <p className="sub">Pausing stops matching entirely, and nothing is backfilled.</p>
      <ul>
        <li>Matching stops. No seeds are spent.</li>
        <li>Stories that break while paused are not recoverable.</li>
        <li>Alerts already in your queue stay, and remain buildable.</li>
        <li>Your watches and your alert budget are untouched.</li>
      </ul>
      <div className="two">
        <button type="button" onClick={onClose}>Keep watching</button>
        <button type="button" className="pri" onClick={() => { set.mutate({ paused: true }); onClose(); toast(<>Paused. <b>Your alert budget and watches are kept.</b></>) }}>Pause</button>
      </div>
    </Sheet>
  )
}

// §8.1: a subject plus a condition from the curated library; your own words allowed, flagged looser
function AddSheet({ type, onClose, toast }: { type: WatchTypeId; onClose: () => void; toast: (b: React.ReactNode) => void }) {
  const T = WATCH_TYPES[type]
  const add = useAddWatch()
  const [subj, setSubj] = useState('')
  const [cond, setCond] = useState<string>(T.conditions[0])
  const [own, setOwn] = useState(false)
  const [ownT, setOwnT] = useState('')
  const ok = !!subj.trim() && (!own || !!ownT.trim())
  const go = () => {
    const c = own ? ownT.trim() : cond
    add.mutate({ type, subject: subj.trim(), condition: c, own }, {
      onSuccess: () => { onClose(); toast(<>Watching <b>{subj.trim()} · {c}</b>.</>) },
      onError: (e) => toast((e as Error).message),
    })
  }
  return (
    <Sheet open onClose={onClose} label={`Watch in ${T.name}`}>
      <h3>Watch in {T.name}</h3>
      <p className="sub">A subject and what happens to it. “Taylor Swift” alone would fire every day; “Taylor Swift · pregnancy” fires when it happens.</p>
      <div className="k">Subject</div>
      <input type="text" autoFocus value={subj} onChange={(e) => setSubj(e.target.value)} maxLength={120}
        placeholder={T.scope === 'local' ? 'e.g. Deschutes County Sheriff' : 'e.g. a person, a team, a company'} autoComplete="off" />
      <div className="k">Condition</div>
      <div className="gga-conds">
        {T.conditions.map((c) => <button key={c} type="button" aria-pressed={!own && c === cond} onClick={() => { setOwn(false); setCond(c) }}>{c}</button>)}
        <button type="button" className="own" aria-pressed={own} onClick={() => setOwn(true)}>Your own words</button>
      </div>
      {own && (
        <>
          <input type="text" value={ownT} onChange={(e) => setOwnT(e.target.value)} placeholder="the words to match" style={{ marginTop: 9 }} autoComplete="off" maxLength={120} />
          <p className="gga-loose"><b>Looser.</b> Matches only what you type, with no protection against old stories or speculation. The library conditions carry that protection.</p>
        </>
      )}
      <button type="button" className="go" disabled={!ok || add.isPending} onClick={go}>Add watch</button>
    </Sheet>
  )
}

// §6: only the types that break
function AnotherSheet({ open, w, onClose, onPick }: { open: boolean; w: WatchState; onClose: () => void; onPick: (t: WatchTypeId) => void }) {
  const addType = useAddWatchType()
  const all = Object.entries(WATCH_TYPES) as [WatchTypeId, (typeof WATCH_TYPES)[WatchTypeId]][]
  const opt = ([k, T]: [WatchTypeId, (typeof WATCH_TYPES)[WatchTypeId]]) => {
    const have = w.types.includes(k), noFeed = T.scope === 'local' && !w.local
    return (
      <button key={k} type="button" className="gga-opt" disabled={have || noFeed}
        onClick={() => addType.mutate(k, { onSuccess: () => { onClose(); setTimeout(() => onPick(k), 200) } })}>
        <TopicGlyph topic={k} size={26} />
        <span>{T.name}<small>{have ? 'watching' : T.scope === 'local' ? 'your local feed' : 'national'}</small></span>
      </button>
    )
  }
  return (
    <Sheet open={open} onClose={onClose} label="Watch another content type">
      <h3>Watch another content type</h3>
      <p className="sub">Only the types that break. National types watch 20 to 30 outlets for everyone; local ones watch your county’s own feeds, where a place you cover has one.</p>
      <div className="k">National</div>
      <div className="gga-grid">{all.filter(([, T]) => T.scope === 'national').map(opt)}</div>
      <div className="k">Local</div>
      <div className="gga-grid">{all.filter(([, T]) => T.scope === 'local').map(opt)}</div>
    </Sheet>
  )
}

/** Brian's test button ("Send a test alert"), for admins and local development. */
function DevTest() {
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const test = useSendTestAlert()
  const scan = useScanNow()
  const [msg, setMsg] = useState('A match lands: 1 seed from the alert budget, and the push.')
  if (!isAdmin && !import.meta.env.DEV) return null
  return (
    <div className="gga-dev">
      <div className="k">Breaking news · test</div>
      <div className="row">
        <button type="button" className="gga-btn" disabled={test.isPending} onClick={() => test.mutate(undefined, {
          onSuccess: (r) => setMsg(!r.ok ? (r.why === 'paused' ? 'Paused: nothing is matched, nothing is spent.' : 'The alert budget is empty: matching has stopped.')
            : `Delivered: 1 seed from the alert budget (${r.bank} / ${r.cap} left)${r.topped ? `, then auto top-up added ${r.topped}` : ''}.`),
          onError: (e) => setMsg((e as Error).message),
        })}>Send a test alert</button>
        <button type="button" className="gga-btn" disabled={scan.isPending} onClick={() => { setMsg('Scanning your watches now. This takes up to a minute.'); scan.mutate(undefined, {
          onSuccess: (r) => setMsg(r.skipped ? `Skipped: ${r.skipped}.` : `Scanned: ${r.matched ?? 0} matched, ${r.delivered ?? 0} delivered.${r.stopped ? ` Stopped: ${r.stopped}.` : ''}`),
          onError: (e) => setMsg((e as Error).message),
        }) }}>Scan my watches now</button>
      </div>
      <p>{msg}</p>
    </div>
  )
}
